import { useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import Table from '../../components/common/Table'
import SearchBar from '../../components/common/SearchBar'
import Button from '../../components/common/Button'
import Modal from '../../components/common/Modal'
import {
  ArrowDownTrayIcon,
  CreditCardIcon,
  EyeIcon,
  EyeSlashIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ayudaApi } from '../../services/api'
import { useQuery } from 'react-query'

function ResidentTransactionPage() {
  const user = useAuthStore((state) => state.user)

  const [query, setQuery] = useState('')
  const [maskBalance, setMaskBalance] = useState(true)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    program: '',
    activity: '',
    dateRange: '',
    amountRange: '',
  })

  const residentId = user?.resident_id ?? user?.id ?? null
  console.log('user', user)
  // const getResidentTransactions = async () => {
  //   return {
  //     items: dummyTransactions,
  //     pagination: {
  //       currentPage: currentPage,
  //       lastPage: Math.ceil(dummyTransactions.length / perPage),
  //       perPage: perPage,
  //       total: dummyTransactions.length,
  //     },
  //   }
  // }

  // useEffect(() => {
  //   let cancelled = false

  //   const load = async () => {
  //     try {
  //       setLoading(true)
  //       setError(null)

  //       if (!residentId) {
  //         throw new Error("Resident ID not found")
  //       }

  //       const res = await getResidentTransactions(residentId)
  //       if (cancelled) return
  //       setTransactions(res?.items || [])
  //     } catch (e) {
  //       if (cancelled) return
  //       setError(e?.message || "Failed to load transactions")
  //     } finally {
  //       if (cancelled) return
  //       setLoading(false)
  //     }
  //   }

  //   load()
  //   return () => {
  //     cancelled = true
  //   }
  // }, [residentId, currentPage, perPage])
  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useQuery({
    queryKey: ['resident-wallet', residentId, currentPage, perPage, query],
    enabled: !!residentId,
    keepPreviousData: true,
    queryFn: async () => {
      const res = await ayudaApi.getResidentWalletTransactions(residentId, {
        page: currentPage,
        per_page: perPage,
        search: query || undefined,
      })

      return res?.data?.data
    },
  })
  const walletBalance = useMemo(() => {
    return Number((data?.wallet_balance || '0').replace(/,/g, ''))
  }, [data])
  const resident = data?.resident
  const displayName = resident?.full_name || 'RESIDENT'
  const barangayId = resident?.barangay_id_number || '-'
  const transactions = useMemo(() => {
    return (data?.wallet_transactions || []).map((t) => ({
      id: t.id,
      transactionNo: t.transaction_number,
      date: new Date(t.transaction_date).toLocaleString('en-PH', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      program: t.ayuda_program?.title || '-',
      activity: t.activity_status || '-',
      amount: Number((t.amount || '0').replace(/,/g, '')),
    }))
  }, [data])
  const pagination = useMemo(() => {
    return {
      currentPage: data?.meta?.current_page || 1,
      lastPage: data?.meta?.last_page || 1,
      perPage: data?.meta?.per_page || 10,
      total: data?.meta?.total || 0,
    }
  }, [data])
  const filtered = useMemo(() => {
    let filteredTransactions = transactions

    // Apply search query filter
    const q = query.trim().toLowerCase()
    if (q) {
      filteredTransactions = filteredTransactions.filter((t) => {
        const hay = [t.transactionNo, t.date, t.program, t.activity, t.amount]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return hay.includes(q)
      })
    }

    // Apply program filter
    if (filters.program) {
      filteredTransactions = filteredTransactions.filter((t) =>
        t.program.toLowerCase().includes(filters.program.toLowerCase())
      )
    }

    // Apply activity filter
    if (filters.activity) {
      filteredTransactions = filteredTransactions.filter((t) =>
        t.activity.toLowerCase().includes(filters.activity.toLowerCase())
      )
    }

    // Apply amount range filter
    if (filters.amountRange) {
      const [min, max] = filters.amountRange
        .split('-')
        .map((v) => parseFloat(v.trim()))
      if (!isNaN(min) && !isNaN(max)) {
        filteredTransactions = filteredTransactions.filter(
          (t) => t.amount >= min && t.amount <= max
        )
      }
    }

    return filteredTransactions
  }, [query, transactions, filters])

  useEffect(() => {
    setCurrentPage(1)
  }, [query, transactions, filters])

  const paginatedData = useMemo(() => {
    const safeCurrentPage = pagination.currentPage
    const start = (safeCurrentPage - 1) * perPage
    const end = start + perPage
    return (filtered || []).slice(start, end)
  }, [filtered, pagination.currentPage, perPage])
  const columns = useMemo(
    () => [
      { key: 'transactionNo', label: 'Transaction No.' },
      { key: 'date', label: 'Date' },
      { key: 'program', label: 'Program' },
      { key: 'activity', label: 'Activity' },
      {
        key: 'amount',
        label: 'Amount',
        // render: (value) => `₱${Number(value || 0).toFixed(2)}`,
        render: (value) =>
          `₱${Number(value || 0).toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              size='sm'
              icon={<EyeIcon className='h-4 w-4 text-blue-600' />}
              onClick={() => {
                setSelectedTransaction(row)
                setIsDetailsOpen(true)
              }}
              aria-label={`View transaction ${
                row?.transactionNo || row?.id || ''
              }`}
            />
          </div>
        ),
      },
    ],
    []
  )

  // Get unique programs for filter dropdown
  const uniquePrograms = useMemo(() => {
    return [...new Set(transactions.map((t) => t.program))].filter(Boolean)
  }, [transactions])

  // Get unique activities for filter dropdown
  const uniqueActivities = useMemo(() => {
    return [...new Set(transactions.map((t) => t.activity))].filter(Boolean)
  }, [transactions])

  const clearFilters = () => {
    setFilters({
      program: '',
      activity: '',
      dateRange: '',
      amountRange: '',
    })
  }

  const hasActiveFilters = Object.values(filters).some((value) => value !== '')

  const accountNumber = '******9350'
  // const lastActivity = transactions?.[0]?.date || "-"
  // const accountBalance = transactions?.reduce(
  //   (sum, t) => sum + Number(t?.amount || 0),
  //   0
  // )
  // const lastActivity = transactions?.length ? transactions[0].date : '-'
  const lastActivity = useMemo(() => {
    const rawDate = data?.wallet_transactions?.[0]?.created_at
    if (!rawDate) return '-'

    return new Date(rawDate.replace(' ', 'T')).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }, [data])

  const accountBalance = walletBalance

  return (
    <div className='w-auto'>
      <div className='flex items-start gap-3'>
        <div className='h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center'>
          <CreditCardIcon className='h-5 w-5 text-blue-700' />
        </div>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>Wallet</h2>
          <p className='text-sm text-gray-500'>
            Review wallet activity, including received and withdrawn assistance.
          </p>
        </div>
      </div>

      <div className='mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl'>
        <div className='relative overflow-hidden rounded-2xl p-6 text-white shadow-sm border border-blue-200 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700'>
          <div className='absolute inset-0 opacity-20'>
            <div className='absolute -right-24 top-8 h-64 w-64 rounded-full border border-white/30' />
            <div className='absolute -right-10 top-20 h-52 w-52 rounded-full border border-white/20' />
            <div className='absolute -left-24 -bottom-24 h-80 w-80 rounded-full border border-white/20' />
          </div>

          <div className='relative'>
            <div className='text-lg font-semibold tracking-wide'>
              {displayName}
            </div>

            <div className='mt-6 flex items-center gap-2 text-sm text-white/90'>
              <span>Account Balance</span>
              <button
                type='button'
                onClick={() => setMaskBalance((v) => !v)}
                className='rounded-md p-1 hover:bg-white/10'
                aria-label={maskBalance ? 'Show balance' : 'Hide balance'}
              >
                {maskBalance ? (
                  <EyeSlashIcon className='h-4 w-4' />
                ) : (
                  <EyeIcon className='h-4 w-4' />
                )}
              </button>
            </div>

            {/* <div className="mt-2 text-2xl font-semibold">
              {maskBalance
                ? "₱ • • • •"
                : `₱${Number(accountBalance || 0).toFixed(2)}`}
            </div> */}

            <div className='mt-2 text-2xl font-semibold'>
              {maskBalance
                ? '₱ • • • •'
                : `₱${Number(accountBalance || 0).toLocaleString('en-PH', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
            </div>

            <div className='mt-6 text-sm text-white/90'>
              Account Number: {accountNumber}
            </div>
            <div className='mt-2 text-xs text-white/70'>
              Last Activity: {lastActivity}
            </div>

            <p className='text-white/70 text-xs mt-2 '>
              Wallet ID:{''}
              <a
                href={data?.resident?.wallet_url}
                target='_blank'
                className='hover:underline hover:text-blue-400 hover:cursor-pointer'
              >
                <span className='ml-2'>{data?.resident?.wallet}</span>
              </a>
            </p>
          </div>
        </div>

        <button
          type='button'
          className='group flex min-h-[200px] items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700'
          onClick={() => console.log('New Disbursement Account')}
        >
          <div className='flex flex-col items-center gap-2'>
            <span className='flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 group-hover:bg-gray-100'>
              <PlusIcon className='h-5 w-5' />
            </span>
            <span className='text-sm font-medium'>
              New Disbursement Account
            </span>
          </div>
        </button>
      </div>

      <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-5'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='text-base font-semibold text-gray-900'>
            Transactions
          </div>

          <div className='flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3'>
            <div className='w-full sm:w-[320px]'>
              <SearchBar
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Search transactions...'
              />
            </div>

            <Button
              variant='outline'
              size='md'
              icon={<FunnelIcon className='h-4 w-4' />}
              onClick={() => setShowFilters(!showFilters)}
              className={
                hasActiveFilters
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : ''
              }
            >
              Filters{' '}
              {hasActiveFilters &&
                `(${Object.values(filters).filter((v) => v !== '').length})`}
            </Button>
            <Button
              variant='primary'
              size='md'
              icon={<ArrowDownTrayIcon className='h-4 w-4' />}
              onClick={() => console.log('Export')}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Inline Filters */}
        {showFilters && (
          <div className='mt-4 p-4'>
            <div className='flex justify-end mb-4'>
              <Button
                variant='ghost'
                size='sm'
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className='text-gray-500 hover:text-gray-700'
              >
                Clear All
              </Button>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              {/* Program Filter */}
              <div>
                <label className='block text-xs font-medium text-gray-600 mb-1'>
                  Program
                </label>
                <select
                  value={filters.program}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, program: e.target.value }))
                  }
                  className='mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                >
                  <option value=''>All Programs</option>
                  {uniquePrograms.map((program) => (
                    <option key={program} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
              </div>

              {/* Activity Filter */}
              <div>
                <label className='block text-xs font-medium text-gray-600 mb-1'>
                  Activity
                </label>
                <select
                  value={filters.activity}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      activity: e.target.value,
                    }))
                  }
                  className='mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                >
                  <option value=''>All Activities</option>
                  {uniqueActivities.map((activity) => (
                    <option key={activity} value={activity}>
                      {activity}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount Range Filter */}
              <div>
                <label className='block text-xs font-medium text-gray-600 mb-1'>
                  Amount Range
                </label>
                <select
                  value={filters.amountRange}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      amountRange: e.target.value,
                    }))
                  }
                  className='mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                >
                  <option value=''>Any Amount</option>
                  <option value='0-1000'>₱0 - ₱1,000</option>
                  <option value='1000-5000'>₱1,000 - ₱5,000</option>
                  <option value='5000-10000'>₱5,000 - ₱10,000</option>
                  <option value='10000-50000'>₱10,000 - ₱50,000</option>
                  <option value='50000-999999'>₱50,000+</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className='mt-4 text-sm text-gray-600'>Loading...</div>
        ) : error ? (
          <div className='mt-4 text-sm text-red-600'>{error}</div>
        ) : (
          <div className='mt-4'>
            <Table
              columns={columns}
              data={transactions}
              noContainer
              getRowKey={(row) => row.id}
              pagination={pagination}
              onPageChange={(page, nextPerPage) => {
                if (
                  typeof nextPerPage === 'number' &&
                  nextPerPage !== perPage
                ) {
                  setPerPage(nextPerPage)
                }
                setCurrentPage(page)
              }}
              paginationProps={{
                isLoading: loading,
              }}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false)
          setSelectedTransaction(null)
        }}
        title={null}
        showCloseButton={false}
        size='4xl'
      >
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <span className='inline-flex items-center rounded-md bg-blue-600 px-3 py-1 text-sm font-semibold text-white'>
              {selectedTransaction?.transactionNo ||
                selectedTransaction?.id ||
                '-'}
            </span>
            <div className='text-xl font-semibold text-gray-900'>
              Transaction Details
            </div>
          </div>

          <button
            type='button'
            onClick={() => {
              setIsDetailsOpen(false)
              setSelectedTransaction(null)
            }}
            className='p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200'
            aria-label='Close modal'
          >
            <XMarkIcon className='h-6 w-6 text-gray-500' />
          </button>
        </div>

        <div className='mt-4 rounded-2xl bg-gray-50 p-6'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <div>
              <div className='text-sm font-semibold text-gray-500'>
                Program Name
              </div>
              <div className='mt-2 text-sm text-gray-900'>
                {selectedTransaction?.program || '-'}
              </div>
            </div>

            <div>
              <div className='text-sm font-semibold text-gray-500'>Date</div>
              <div className='mt-2 text-sm text-gray-900'>
                {selectedTransaction?.date || '-'}
              </div>
            </div>

            <div>
              <div className='text-sm font-semibold text-gray-500'>Amount</div>
              <div className='mt-2 text-sm text-gray-900'>
                {/* ₱{Number(selectedTransaction?.amount || 0).toFixed(2)} */}₱
                {Number(selectedTransaction?.amount || 0).toLocaleString(
                  'en-PH',
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </div>
            </div>

            <div>
              <div className='text-sm font-semibold text-gray-500'>
                Activity
              </div>
              <div className='mt-2 text-sm text-gray-900'>
                {selectedTransaction?.activity || '-'}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ResidentTransactionPage
